import useSWR, { mutate } from 'swr'
import { apiFetch } from '@/utils'
import type { SavedCard, AddCardRequest, UpdateCardRequest } from '@/types/payment-methods'

const BILLING_CARDS_KEY = '/v1/billing/cards'

/** Normalize backend response — expiry may come as string */
function normalizeSavedCard(raw: any): SavedCard {
	return {
		...raw,
		type: 'CARD' as const,
		expiryMonth: raw.expiryMonth != null ? Number(raw.expiryMonth) : 0,
		expiryYear: raw.expiryYear != null ? Number(raw.expiryYear) : 0,
		billing: raw.billing ?? null,
		updateRequired: raw.updateRequired ?? raw.update_required ?? false,
	}
}

export function usePaymentMethods() {
	const { data, error, isLoading } = useSWR<SavedCard[]>(
		BILLING_CARDS_KEY,
		async (path: string) => {
			const res = await apiFetch(path, { withCredentials: true })
			if (!res.ok) {
				// Return empty if endpoint not built yet (404)
				if (res.status === 404) return []
				throw new Error('Failed to fetch payment methods')
			}
			const json = await res.json()
			const cards: any[] = json.data ?? json
			return cards.map(normalizeSavedCard)
		},
		{
			revalidateOnFocus: false,
			dedupingInterval: 10_000,
			// Gracefully handle missing backend
			shouldRetryOnError: false,
		}
	)

	const cards: SavedCard[] = data ?? []

	const addCard = async (req: AddCardRequest): Promise<SavedCard> => {
		const res = await apiFetch(BILLING_CARDS_KEY, {
			method: 'POST',
			body: JSON.stringify(req),
			withCredentials: true,
		})
		if (!res.ok) {
			const err = await res.json().catch(() => null)
			throw new Error(err?.message || 'Failed to add card')
		}
		const json = await res.json()
		await mutate(BILLING_CARDS_KEY)
		return normalizeSavedCard(json.data ?? json)
	}

	const updateCard = async (cardId: number, req: UpdateCardRequest): Promise<SavedCard> => {
		const res = await apiFetch(`${BILLING_CARDS_KEY}/${cardId}`, {
			method: 'PUT',
			body: JSON.stringify(req),
			withCredentials: true,
		})
		if (!res.ok) {
			const err = await res.json().catch(() => null)
			throw new Error(err?.message || 'Failed to update card')
		}
		const json = await res.json()
		await mutate(BILLING_CARDS_KEY)
		return normalizeSavedCard(json.data ?? json)
	}

	const setDefaultCard = async (cardId: number): Promise<SavedCard> => {
		const res = await apiFetch(`${BILLING_CARDS_KEY}/${cardId}/default`, {
			method: 'PUT',
			withCredentials: true,
		})
		if (!res.ok) {
			const err = await res.json().catch(() => null)
			throw new Error(err?.message || 'Failed to set default card')
		}
		const json = await res.json()
		await mutate(BILLING_CARDS_KEY)
		return normalizeSavedCard(json.data ?? json)
	}

	const deleteCard = async (cardId: number): Promise<void> => {
		const res = await apiFetch(`${BILLING_CARDS_KEY}/${cardId}`, {
			method: 'DELETE',
			withCredentials: true,
		})
		if (!res.ok) {
			const err = await res.json().catch(() => null)
			throw new Error(err?.message || 'Failed to delete card')
		}
		await mutate(BILLING_CARDS_KEY)
	}

	const isCardExpired = (card: SavedCard): boolean => {
		const now = new Date()
		const currentYear = now.getFullYear()
		const currentMonth = now.getMonth() + 1 // 1-indexed
		if (!card.expiryYear || !card.expiryMonth) return false
		// A card in the current month is still valid (expires at end of month)
		if (card.expiryYear < currentYear) return true
		if (card.expiryYear === currentYear && card.expiryMonth < currentMonth) return true
		return false
	}

	return {
		cards,
		isLoading,
		error,
		addCard,
		updateCard,
		setDefaultCard,
		deleteCard,
		isCardExpired,
		refetch: () => mutate(BILLING_CARDS_KEY),
	}
}
